import dayjs from 'dayjs';
import isNaN from 'lodash/isNaN';
import isNumber from 'lodash/isNumber';
import morphismPackage from 'morphism';
import qs from 'qs';

import {
  API_ERRORS,
  AUTOMATION_CREATION_STEPS,
  AUTOMATION_STATUSES,
  dateLabelFormatDayWithYear,
  MAX_FEATURE_NAME_LENGTH,
  MIN_LENGTH,
  ORDER_DELIVERED,
  ORDER_STATUSES,
  RECOGNITION_CREATION_STEPS,
  RECOGNITION_STATUSES
} from '@hero/constants';

import { cleanObjectEmptyFields } from './clearObject';
import { isFloat } from './validationUtils';

const addPaymentMethodLink = (providerUrl, currentPath) => {
  const transactionId = Math.floor(Math.random() * 100000) + 1;
  const url = `${providerUrl}?redirect_url=addnewcc&callback_url=${currentPath}&transaction_id=${transactionId}`;

  if (providerUrl) {
    return url;
  }

  return currentPath;
};

const editPaymentMethodLink = (providerUrl, currentPath, cardId) => {
  const transactionId = Math.floor(Math.random() * 100000) + 1;
  const url = `${providerUrl}?redirect_url=addnewcc&card_id=${cardId}&callback_url=${currentPath}&transaction_id=${transactionId}`;

  if (providerUrl) {
    return url;
  }
  return currentPath;
};

const calcPercentage = (value = 0, total = 1) => (value / total) * 100;

const getAnalyticRates = (analytics = {}, { format = false }) => {
  const { accepted, opened, total } = analytics;
  const unopened = total - opened;
  const unaccepted = total - accepted;

  if (format) {
    return {
      accepted: formatPercentage(calcPercentage(accepted, total)),
      unaccepted: formatPercentage(calcPercentage(unaccepted, total)),
      opened: formatPercentage(calcPercentage(opened, total)),
      unopened: formatPercentage(calcPercentage(unopened, total))
    };
  }

  return {
    accepted: calcPercentage(accepted, total),
    opened: calcPercentage(opened, total),
    unopened: calcPercentage(unopened, total),
    unaccepted: calcPercentage(unaccepted, total)
  };
};

const checkApiError = error => {
  const errorObject = API_ERRORS[error];
  return !!errorObject;
};

const convertHtmlMessageToString = html => {
  if (!html) return '';

  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;

  let text = tempDiv.innerText || tempDiv.textContent || '';

  text = text.replace(/\n+/g, '\n').trim();

  return text;
};

const formatCurrency = (value = 0, { locale = 'en-US', currency = 'USD' } = {}) =>
  new Intl.NumberFormat(locale, {
    style: 'currency',
    currency
  }).format(value);

const formatDate = (
  date,
  { format = dateLabelFormatDayWithYear, includeTime = true, includeZone = false } = {}
) => {
  let _dateFormat = format;

  if (includeTime) {
    _dateFormat = `${format} hh:mm A`;
    if (includeZone) {
      _dateFormat = `${_dateFormat} (Z)`;
    }
  }

  if (date) {
    if (date.includes && !date.includes('Z')) {
      return dayjs(typeof date === 'string' ? new Date(date) : date)
        .utc()
        .format(_dateFormat);
    }

    return dayjs(date).format(_dateFormat);
  }

  return undefined;
};

const formatNumber = (value = 0) => {
  if (!value || !isNumber(value) || isNaN(value)) return 0;
  else return value;
};

const formatPercentage = number => {
  if (number) {
    return `${Number(number).toFixed(isFloat(Number(number)) ? 2 : 1)}%`;
  }
  return '0.0%';
};

const formatPhoneNumber = str => {
  let cleaned = ('' + str).replace(/\D/g, '');

  let match = /^(1)?(\d{3})(\d{3})(\d{4})$/.exec(cleaned);

  if (match) {
    let intlCode = match[1] ? '+1 ' : '';
    return [intlCode, '(', match[2], ') ', match[3], '-', match[4]].join('');
  }

  return null;
};

const getGiftStatus = ({ status, giftStatus, deliveryDate }) => {
  if (
    (status?.injected || !Object.prototype.hasOwnProperty.call(status, ORDER_STATUSES.injected)) &&
    new Date() > new Date(deliveryDate)
  )
    return ORDER_DELIVERED;

  return giftStatus;
};

const isEnter = event => event && (event.key === 'Enter' || event.keyCode === 13);

const trimStringsInObject = obj => {
  Object.keys(obj).forEach(k => (obj[k] = obj[k].toString().trim()));
  return obj;
};

const isValidUrl = string => {
  try {
    return Boolean(new URL(string));
  } catch (error) {
    return false;
  }
};

const getRedirectUrl = (url, params) => {
  if (!isValidUrl(url)) {
    return new URL(window.location.origin);
  }

  const queryString = qs.stringify(cleanObjectEmptyFields(params), { addQueryPrefix: true });
  const redirectUrl = `${url}${queryString}`;
  const newUrl = new URL(redirectUrl);

  newUrl.pathname = newUrl.pathname.replace('/dashboard', '');

  return newUrl;
};

const getFeatureStatus = feature => {
  if (
    feature?.status?.current === RECOGNITION_STATUSES.draft &&
    feature.meta?.creationStep === RECOGNITION_CREATION_STEPS.newRecognitionReviewPay
  )
    return RECOGNITION_STATUSES.readyToSend;

  if (
    feature?.status === AUTOMATION_STATUSES.draft &&
    AUTOMATION_CREATION_STEPS.newAutomationReviewActive
  )
    return AUTOMATION_STATUSES.readyToActivate;

  return feature?.status?.current || feature?.status;
};

const getAddonData = (addons, type) => {
  return addons?.find(item => item.type === type)?.data;
};

const hasAnyAvailableProducts = products => {
  return products?.filter(product => product.orderable).length > 0;
};

const getFormattedPrice = prices => {
  const isPriceRange = prices?.min !== prices?.max;

  if (isPriceRange) {
    return `${formatCurrency(Number(prices?.min))} - ${formatCurrency(Number(prices?.max))}`;
  }

  return formatCurrency(prices?.min);
};

const morphism = (schema, data) => cleanObjectEmptyFields(morphismPackage(schema, data));

const getNameFeatureFormData = ({ label, value }) => {
  return [
    {
      name: 'name',
      type: 'text',
      value,
      label,
      minLength: MIN_LENGTH,
      maxLength: MAX_FEATURE_NAME_LENGTH,
      helperProps: {
        showCounter: true
      }
    }
  ];
};

const parseContactName = fullName => {
  if (fullName) {
    const parts = fullName.trim().split(/\s+/);

    const firstName = parts[0];
    const lastName = parts.slice(1).join(' ');

    return cleanObjectEmptyFields({ firstName, lastName });
  }
  return fullName;
};

export {
  addPaymentMethodLink,
  checkApiError,
  convertHtmlMessageToString,
  editPaymentMethodLink,
  formatCurrency,
  formatDate,
  formatNumber,
  formatPercentage,
  formatPhoneNumber,
  getAddonData,
  getAnalyticRates,
  getFeatureStatus,
  getFormattedPrice,
  getGiftStatus,
  getNameFeatureFormData,
  getRedirectUrl,
  hasAnyAvailableProducts,
  isEnter,
  morphism,
  parseContactName,
  trimStringsInObject
};
